import React, { useEffect, useMemo, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import API from "../../api";
import { getWhatsAppSettings } from "../../api/whatsapp";
import { getEmailSettings } from "../../api/integrations";
import PageHeaderSection from "../../components/layout/PageHeaderSection";
import PageLoadingShell from "../../components/layout/PageLoadingShell";
import { TextInput } from "../../components/common/form";
import { useUnsavedChanges } from "../../context/UnsavedChangesContext";

function StatusChip({ on, t }) {
    return (
        <span
            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${
                on ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"
            }`}
        >
            {on ? t("settings.report.statusOn") : t("settings.report.statusOff")}
        </span>
    );
}

function PreferenceSwitch({ checked, onChange, disabled, label }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            aria-label={label}
            disabled={disabled}
            onClick={() => !disabled && onChange()}
            className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer"
            } ${checked ? "bg-primary-600" : "bg-slate-200"}`}
        >
            <span
                className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                    checked ? "translate-x-5" : "translate-x-0"
                }`}
            />
        </button>
    );
}

function ToggleCard({ label, description, checked, onToggle, disabled, master, t }) {
    return (
        <label
            className={`flex cursor-pointer gap-3 rounded-xl border p-4 transition-colors ${
                disabled ? "cursor-not-allowed opacity-60" : ""
            } ${
                checked
                    ? "border-primary-200 bg-primary-50/40 hover:border-primary-300"
                    : "border-slate-200 bg-slate-50/60 hover:border-slate-300"
            } ${master ? "md:col-span-2" : ""}`}
        >
            <input
                type="checkbox"
                className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                checked={checked}
                disabled={disabled}
                onChange={onToggle}
            />
            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                    <span className={`text-sm ${master ? "font-semibold" : "font-medium"} text-slate-900`}>
                        {label}
                    </span>
                    <StatusChip on={checked} t={t} />
                </div>
                {description && (
                    <p className="mt-1 text-sm leading-snug text-slate-600">{description}</p>
                )}
            </div>
        </label>
    );
}

function PreferenceRow({ label, checked, onToggle, disabled, master }) {
    const { t } = useTranslation();
    return (
        <div
            className={`flex items-center justify-between gap-4 rounded-xl border p-4 ${
                checked
                    ? "border-primary-200 bg-primary-50/40"
                    : "border-slate-200 bg-slate-50/60"
            } ${disabled && !master ? "opacity-60" : ""} ${master ? "border-primary-200 bg-primary-50/50" : ""}`}
        >
            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                    <p className={`text-sm ${master ? "font-semibold" : "font-medium"} text-slate-900`}>{label}</p>
                    <StatusChip on={checked} t={t} />
                </div>
            </div>
            <PreferenceSwitch checked={checked} onChange={onToggle} disabled={disabled} label={label} />
        </div>
    );
}

export default function NotificationPreferences() {
    const { t } = useTranslation();
    const [prefs, setPrefs] = useState(null);
    const [waLicensed, setWaLicensed] = useState(false);
    const [smtpOperational, setSmtpOperational] = useState(false);
    const [channelStatusLoadError, setChannelStatusLoadError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState("");
    const [msgError, setMsgError] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                let channelFailed = false;
                const [prefRes, waRes, emailRes] = await Promise.all([
                    API.get("/admin/notification-preferences"),
                    getWhatsAppSettings().catch(() => { channelFailed = true; return null; }),
                    getEmailSettings().catch(() => { channelFailed = true; return null; }),
                ]);
                setPrefs(prefRes.data);
                setWaLicensed(!!waRes?.licenseWhatsAppEnabled);
                setSmtpOperational(!!emailRes?.operational);
                setChannelStatusLoadError(channelFailed);
            } catch {
                setMsg(t('settings.notifications.loadFailed', { defaultValue: 'Could not load notification preferences.' }));
                setMsgError(true);
            } finally {
                setLoading(false);
            }
        })();
    }, [t]);

    const EMAIL_TOGGLES = useMemo(() => [
        { key: "emailEnabled", labelKey: "emailMaster", master: true },
        { key: "emailFeeReminders", labelKey: "emailFeeReminders" },
        { key: "emailSessionRemindersStudent", labelKey: "emailSessionRemindersStudent" },
        { key: "emailSessionRemindersCoach", labelKey: "emailSessionRemindersCoach" },
        { key: "emailAbsenceAlerts", labelKey: "emailAbsenceAlerts" },
        { key: "emailSessionCancelled", labelKey: "emailSessionCancelled" },
        { key: "emailOnlineTopupAdmin", labelKey: "emailOnlineTopupAdmin" },
    ].map((item) => ({ ...item, label: t(`settings.notifications.${item.labelKey}`) })), [t]);

    const INAPP_TOGGLES = useMemo(() => [
        { key: "inappEnabled", labelKey: "inappMaster", master: true },
        { key: "inappFeeAlerts", labelKey: "inappFeeAlerts" },
        { key: "inappPendingSessions", labelKey: "inappPendingSessions" },
        { key: "inappAtRiskStudents", labelKey: "inappAtRiskStudents" },
    ].map((item) => ({ ...item, label: t(`settings.notifications.${item.labelKey}`) })), [t]);

    const WHATSAPP_STUDENT = useMemo(() => [
        { key: "whatsappSessionRemindersStudent", labelKey: "waSessionReminders" },
        { key: "whatsappAbsenceAlertsStudent", labelKey: "waAbsenceAlerts" },
        { key: "whatsappLowBalanceStudent", labelKey: "waLowBalance" },
        { key: "whatsappFeeRemindersStudent", labelKey: "waFeeReminders" },
        { key: "whatsappSessionCancelledStudent", labelKey: "waSessionCancelled" },
    ].map((item) => ({ ...item, label: t(`settings.notifications.${item.labelKey}`) })), [t]);

    const WHATSAPP_STAFF = useMemo(() => [
        { key: "whatsappPendingSessionsCoach", labelKey: "waPendingSessions" },
        { key: "whatsappLowBalanceAdmin", labelKey: "waLowBalanceAdmin" },
        { key: "whatsappHolidayAnnouncements", labelKey: "waHolidayAnnouncements" },
        { key: "whatsappCorrespondenceDeadline", labelKey: "waCorrespondenceDeadline" },
        { key: "whatsappCorrespondenceYourMove", labelKey: "waCorrespondenceYourMove" },
    ].map((item) => ({ ...item, label: t(`settings.notifications.${item.labelKey}`) })), [t]);

    const toggle = (key) => setPrefs((p) => ({ ...p, [key]: !p[key] }));

    const persist = async () => {
        const res = await API.put("/admin/notification-preferences", prefs);
        setPrefs(res.data);
        return res.data;
    };

    const { markSaved } = useUnsavedChanges(prefs, {
        enabled: !!prefs,
        onSave: async () => { await persist(); },
    });

    const save = async (e) => {
        e?.preventDefault?.();
        setSaving(true);
        setMsg("");
        setMsgError(false);
        try {
            const next = await persist();
            markSaved(next);
            setMsg(t('settings.notifications.saveSuccess'));
        } catch {
            setMsg(t('settings.notifications.saveFailed'));
            setMsgError(true);
        } finally {
            setSaving(false);
        }
    };

    const waDescription = useMemo(() => {
        if (!waLicensed) {
            return t('settings.notifications.whatsappNotLicensed');
        }
        return t('settings.notifications.whatsappDesc');
    }, [waLicensed, t]);

    if (loading) {
        return (
            <PageLoadingShell
                variant="page-shell-form"
                title={t('settings.notifications.title')}
                subtitle={t('settings.notifications.loadingSubtitle')}
            />
        );
    }

    if (!prefs) {
        return (
            <div className="page-shell-form">
                <PageHeaderSection
                    eyebrow={t('nav.system')}
                    title={t('settings.notifications.title')}
                    subtitle={t('settings.notifications.subtitle')}
                />
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="status">
                    {msg || t('settings.notifications.loadFailed', { defaultValue: 'Could not load notification preferences.' })}
                </div>
            </div>
        );
    }

    const emailMasterOn = !!prefs.emailEnabled;
    const inappMasterOn = !!prefs.inappEnabled;
    const waMasterOn = !!prefs.whatsappEnabled;

    return (
        <div className="page-shell-form">
            <PageHeaderSection
                eyebrow={t('nav.system')}
                title={t('settings.notifications.title')}
                subtitle={t('settings.notifications.subtitle')}
            />

            <form onSubmit={save} className="space-y-6">
                {msg && (
                    <div
                        className={`rounded-xl border px-4 py-3 text-sm ${
                            msgError
                                ? "border-red-200 bg-red-50 text-red-800"
                                : "border-emerald-200 bg-emerald-50 text-emerald-800"
                        }`}
                    >
                        {msg}
                    </div>
                )}
                {channelStatusLoadError && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status">
                        {t('settings.notifications.channelStatusLoadFailed', {
                            defaultValue: 'Could not refresh WhatsApp/email channel status. Toggle availability may be outdated — refresh the page.',
                        })}
                    </div>
                )}

                <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
                        <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                            {t('settings.notifications.stepOne')}
                        </p>
                        <h2 className="mt-1 text-lg font-semibold text-slate-900">
                            {t('settings.notifications.emailPanel')}
                        </h2>
                        <p className="mt-1 max-w-2xl text-sm text-slate-600">
                            {t('settings.notifications.emailDesc')}
                        </p>
                    </div>
                    <div className="space-y-4 px-5 py-5 sm:px-6">
                        {!smtpOperational && (
                            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                                <Trans i18nKey="settings.notifications.emailNotConfigured" components={{ strong: <strong /> }} />
                            </div>
                        )}
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            {EMAIL_TOGGLES.map(({ key, label, master }) => (
                                <ToggleCard
                                    key={key}
                                    label={label}
                                    checked={!!prefs[key]}
                                    master={master}
                                    disabled={!smtpOperational || (!master && !emailMasterOn)}
                                    onToggle={() => toggle(key)}
                                    t={t}
                                />
                            ))}
                        </div>
                        <p className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
                            {smtpOperational
                                ? t('settings.notifications.emailFooterOn')
                                : t('settings.notifications.emailFooterOff')}
                        </p>
                    </div>
                </section>

                <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                                    {t('settings.notifications.stepTwo')}
                                </p>
                                <h2 className="mt-1 text-lg font-semibold text-slate-900">
                                    {t('settings.notifications.whatsappPanel')}
                                </h2>
                                <p className="mt-1 max-w-2xl text-sm text-slate-600">{waDescription}</p>
                            </div>
                            {!waLicensed && (
                                <span className="shrink-0 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                                    {t('settings.notifications.notLicensedBadge')}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="space-y-4 px-5 py-5 sm:px-6">
                        <PreferenceRow
                            label={t('settings.notifications.whatsappMaster')}
                            checked={waMasterOn}
                            master
                            disabled={!waLicensed}
                            onToggle={() => toggle("whatsappEnabled")}
                        />
                        <div>
                            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                                {t('settings.notifications.studentsSection')}
                            </p>
                            <div className="space-y-3">
                                {WHATSAPP_STUDENT.map(({ key, label }) => (
                                    <PreferenceRow
                                        key={key}
                                        label={label}
                                        checked={!!prefs[key]}
                                        disabled={!waLicensed || !waMasterOn}
                                        onToggle={() => toggle(key)}
                                    />
                                ))}
                            </div>
                        </div>
                        <div>
                            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                                {t('settings.notifications.staffSection')}
                            </p>
                            <div className="space-y-3">
                                {WHATSAPP_STAFF.map(({ key, label }) => (
                                    <PreferenceRow
                                        key={key}
                                        label={label}
                                        checked={!!prefs[key]}
                                        disabled={!waLicensed || !waMasterOn}
                                        onToggle={() => toggle(key)}
                                    />
                                ))}
                            </div>
                        </div>
                        <p className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
                            {t('settings.notifications.whatsappFooter')}
                        </p>
                    </div>
                </section>

                <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
                        <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                            {t('settings.notifications.stepThree')}
                        </p>
                        <h2 className="mt-1 text-lg font-semibold text-slate-900">
                            {t('settings.notifications.inappPanel')}
                        </h2>
                        <p className="mt-1 max-w-2xl text-sm text-slate-600">
                            {t('settings.notifications.inappDesc')}
                        </p>
                    </div>
                    <div className="grid grid-cols-1 gap-3 px-5 py-5 md:grid-cols-2 sm:px-6">
                        {INAPP_TOGGLES.map(({ key, label, master }) => (
                            <ToggleCard
                                key={key}
                                label={label}
                                checked={!!prefs[key]}
                                master={master}
                                disabled={!master && !inappMasterOn}
                                onToggle={() => toggle(key)}
                                t={t}
                            />
                        ))}
                    </div>
                </section>

                <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
                        <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                            {t('settings.notifications.stepFour')}
                        </p>
                        <h2 className="mt-1 text-lg font-semibold text-slate-900">
                            {t('settings.notifications.walletPanel')}
                        </h2>
                        <p className="mt-1 max-w-2xl text-sm text-slate-600">
                            {t('settings.notifications.walletDesc')}
                        </p>
                    </div>
                    <div className="space-y-4 px-5 py-5 sm:px-6">
                        <TextInput
                            id="low-wallet-threshold"
                            label={t('settings.notifications.lowWalletThreshold')}
                            type="number"
                            step="0.01"
                            min="0"
                            className="max-w-xs"
                            value={prefs.lowWalletThreshold ?? 10}
                            onChange={(e) => setPrefs((p) => ({ ...p, lowWalletThreshold: e.target.value }))}
                            helper={t('settings.notifications.lowWalletHelper')}
                        />
                        <p className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs leading-relaxed text-slate-600">
                            <Trans i18nKey="settings.notifications.walletTip" components={{ strong: <strong /> }} />
                        </p>
                    </div>
                </section>

                <div className="sticky bottom-4 z-10 flex justify-end">
                    <button
                        type="submit"
                        disabled={saving}
                        className="rounded-xl bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-lg hover:bg-primary-700 disabled:opacity-50"
                    >
                        {saving ? t('forms.common.saving') : t('settings.notifications.saveSettings')}
                    </button>
                </div>
            </form>
        </div>
    );
}
